import type { BrandOnboardingMode } from "@runcheetah/shared";
import { useParams } from "react-router";

import { BRAND_APP_ROUTE_MANIFEST } from "@/lib/app-route-manifest";
import { trpc } from "@/lib/trpc";

export const useRouteBrand = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const query = trpc.brand.getById.useQuery(
    { brandId: brandId ?? "" },
    {
      enabled: !!brandId,
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchInterval: (q) => {
        const status = q.state.data?.brand.status;
        const firstAssetReady = q.state.data?.onboarding.completion.firstAssetReady;
        return firstAssetReady || status === "failed" ? false : 2500;
      },
    },
  );

  return {
    brandId,
    brand: query.data?.brand ?? null,
    onboarding: query.data?.onboarding ?? null,
    brandQuery: query,
  };
};

export const buildBrandPath = (brandId: string, section: string): string =>
  `/app/brands/${brandId}${section.startsWith("/") ? section : `/${section}`}`;

export const getUnavailableBrandPath = (brandId: string | null | undefined): string =>
  brandId ? buildBrandPath(brandId, "/onboarding") : "/app";

type BrandStatus = "queued" | "processing" | "scanned" | "failed";

const PRE_APP_READY_BRAND_SECTIONS = BRAND_APP_ROUTE_MANIFEST.flatMap((route) =>
  "availableBeforeAppReady" in route && route.availableBeforeAppReady && route.path
    ? [`/${route.path}`]
    : [],
);

const extractBrandSection = (pathname: string): string | null => {
  const match = pathname.match(/^\/app\/brands\/[^/]+(?<section>\/.*)?$/);
  return match ? (match.groups?.section ?? "/") : null;
};

const isPreAppReadyBrandSection = (section: string): boolean => {
  if (section === "/onboarding") {
    return true;
  }

  return PRE_APP_READY_BRAND_SECTIONS.some(
    (availableSection) =>
      section === availableSection || section.startsWith(`${availableSection}/`),
  );
};

export const isBrandReadyForApp = (input: {
  status: BrandStatus;
  onboardingMode?: BrandOnboardingMode;
  firstAssetReady?: boolean;
}): boolean => {
  return input.status === "scanned";
};

export const isBrandPathAvailableBeforeAppReady = (input: {
  brandId: string | null | undefined;
  status: BrandStatus | null | undefined;
  pathname: string;
}): boolean => {
  if (!input.brandId) {
    return false;
  }

  const basePath = `/app/brands/${input.brandId}`;
  if (input.pathname !== basePath && !input.pathname.startsWith(`${basePath}/`)) {
    return false;
  }

  const section = input.pathname.slice(basePath.length) || "/";
  return (
    section === "/onboarding" || (input.status === "scanned" && isPreAppReadyBrandSection(section))
  );
};

export const getBrandPathForStatus = (input: {
  brandId: string;
  status: BrandStatus;
  onboardingMode?: BrandOnboardingMode;
  firstAssetReady?: boolean;
  currentPathname: string;
}): string => {
  if (!isBrandReadyForApp(input)) {
    const section = extractBrandSection(input.currentPathname);
    if (input.status === "scanned" && section && isPreAppReadyBrandSection(section)) {
      return buildBrandPath(input.brandId, section);
    }

    return buildBrandPath(input.brandId, "/onboarding");
  }

  const section = extractBrandSection(input.currentPathname);
  if (!section || section === "/new" || section === "/onboarding") {
    return buildBrandPath(input.brandId, "/pulse");
  }

  return buildBrandPath(input.brandId, section);
};
